import {
  Box,
  Paper,
  Popover,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { ChipV2, SearchInput } from "components";
import { InfoIcon } from "components/icons";
import {
  useGetChatMembers,
  useUpdateChatMembers,
} from "controller/hooks/chat/useChatMembers";
import { useGetCustomerMembers } from "controller/hooks/customer/useCustomerMembers";
import { useMainStore } from "controller/store/mainStore";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import type { AddMemberItem, ChatMember, CustomerMemberItem } from "shared";
import * as yup from "yup";
import { MemberListItem } from "../ChatMembersDrawer/MemberListItem";

const emailSchema = yup.string().email();

// ─── Pure helpers (same as ChatMembersDrawer) ────────────────────────

function computeMemberDiffs(
  membersData: ChatMember[],
  currentSelectedIds: Set<string>,
): { addedIds: string[]; removableIds: string[] } {
  const initialIds = new Set(membersData.map((m) => m.user_id));
  const coreIds = new Set(
    membersData.filter((m) => m.member_source === "core").map((m) => m.user_id),
  );
  const removableIds = Array.from(initialIds)
    .filter((id) => !currentSelectedIds.has(id))
    .filter((id) => !coreIds.has(id));
  const addedIds = Array.from(currentSelectedIds).filter(
    (id) => !initialIds.has(id),
  );
  return { addedIds, removableIds };
}

function buildAddItems(
  addedIds: string[],
  pendingMap: Map<string, ChatMember>,
  extEmails: string[],
): AddMemberItem[] {
  return [
    ...addedIds.map((id) => ({
      user_id: id,
      member_type: pendingMap.get(id)?.member_type ?? ("customer" as const),
    })),
    ...extEmails.map((email) => ({
      user_id: email,
      member_type: "external" as const,
      email,
    })),
  ];
}

function buildSuccessMessage(result: {
  added_count: number;
  removed_count: number;
}): string[] {
  const parts: string[] = [];
  if (result.added_count > 0)
    parts.push(`${result.added_count} member(s) added`);
  if (result.removed_count > 0) parts.push(`${result.removed_count} removed`);
  return parts;
}

// ─── Sub-components ─────────────────────────────────────────────────

/** Available to add section */
const AvailableToAddList = memo(
  ({
    members,
    selectedIds,
    onToggle,
    threadId,
  }: {
    members: CustomerMemberItem[];
    selectedIds: Set<string>;
    onToggle: (userId: string) => void;
    threadId?: string;
  }) => {
    if (members.length === 0) return null;

    return (
      <>
        {members.map((cm) => (
          <MemberListItem
            key={cm.mapping_id}
            member={{
              user_id: cm.mapping_id,
              display_name: cm.display_name,
              member_type: cm.member_type,
              member_source: "cc",
              email: cm.email,
            }}
            isSelected={selectedIds.has(cm.mapping_id)}
            onToggle={onToggle}
            isLimitedAccess={cm.limited_access === true}
            isInactive={!cm.is_active}
            level={threadId ? "thread" : "ticket"}
            hideEmail
          />
        ))}
      </>
    );
  },
);

AvailableToAddList.displayName = "AvailableToAddList";

// ─── Main Component ─────────────────────────────────────────────────

interface ChatMembersPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  serviceId: string;
  threadId?: string;
}

export const ChatMembersPopover = memo(
  ({
    anchorEl,
    open,
    onClose,
    serviceId,
    threadId,
  }: ChatMembersPopoverProps) => {
    const theme = useTheme();
    const { orange } = theme.palette.primitive.system;

    const customerId = useMainStore(
      (state) => state.ticketDetails?.customerId,
    ) as string | undefined;

    // ─── Local state (replaces shared Jotai atoms) ──────────────────
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [extEmails, setExtEmails] = useState<string[]>([]);
    const [pendingMembers, setPendingMembers] = useState<
      Map<string, ChatMember>
    >(new Map());
    const [initialIds, setInitialIds] = useState<Set<string>>(new Set());
    const [isSaving, setIsSaving] = useState(false);

    // ─── Derived state ──────────────────────────────────────────────
    const hasChanges = useMemo(() => {
      if (extEmails.length > 0) return true;
      if (initialIds.size !== selectedIds.size) return true;
      for (const id of selectedIds) {
        if (!initialIds.has(id)) return true;
      }
      return false;
    }, [extEmails, initialIds, selectedIds]);

    // ─── Local helper callbacks ─────────────────────────────────────
    const toggleSelection = useCallback((userId: string) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(userId)) {
          next.delete(userId);
        } else {
          next.add(userId);
        }
        return next;
      });
    }, []);

    const addExtEmail = useCallback((email: string) => {
      const normalized = email.toLowerCase();
      setExtEmails((prev) =>
        prev.includes(normalized) ? prev : [...prev, normalized],
      );
    }, []);

    const removeExtEmail = useCallback((email: string) => {
      setExtEmails((prev) => prev.filter((e) => e !== email));
    }, []);

    const addToCache = useCallback((member: ChatMember) => {
      setPendingMembers((prev) => {
        const next = new Map(prev);
        next.set(member.user_id, member);
        return next;
      });
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.add(member.user_id);
        return next;
      });
    }, []);

    // ─── Queries ────────────────────────────────────────────────────
    const { data: membersData } = useGetChatMembers(serviceId, threadId);
    const { mutateAsync: updateMembers } = useUpdateChatMembers();

    // Debounced search
    const [debouncedSearch, setDebouncedSearch] = useState("");
    useEffect(() => {
      const timer = setTimeout(() => setDebouncedSearch(searchQuery), 300);
      return () => clearTimeout(timer);
    }, [searchQuery]);

    const { data: customerMembersData, isLoading: isCustomerMembersLoading } =
      useGetCustomerMembers(
        customerId ?? "",
        debouncedSearch || undefined,
        open && Boolean(customerId),
      );

    // ─── Initialize local state from membersData ────────────────────
    useEffect(() => {
      if (membersData && open) {
        const currentIds = new Set(membersData.map((m) => m.user_id));
        setSelectedIds(currentIds);
        setInitialIds(currentIds);
      }
    }, [membersData, open]);

    // ─── Filtered current members ───────────────────────────────────
    const filteredCurrentMembers = useMemo(() => {
      if (!membersData) return [];
      const query = searchQuery.toLowerCase().trim();
      if (!query) return membersData;
      return membersData.filter(
        (m) =>
          (m.display_name ?? "").toLowerCase().includes(query) ||
          (m.email ?? "").toLowerCase().includes(query),
      );
    }, [membersData, searchQuery]);

    // ─── Available customer members (not already in chat) ───────────
    const isDebouncePending = searchQuery !== debouncedSearch;

    const availableCustomerMembers = useMemo((): CustomerMemberItem[] => {
      if (!customerMembersData) return [];

      const existingEmails = new Set(
        (membersData ?? []).map((m) => m.email?.toLowerCase()).filter(Boolean),
      );

      const resources: CustomerMemberItem[] =
        customerMembersData.resources.result
          .filter((r) => !existingEmails.has(r.email?.toLowerCase()))
          .map((r) => ({
            mapping_id: r.mapping_id,
            email: r.email,
            first_name: r.first_name,
            last_name: r.last_name,
            display_name: r.display_name,
            is_active: r.is_active,
            is_locked: r.is_locked,
            member_type: "resource" as const,
            limited_access: r.limited_access,
          }));

      const users: CustomerMemberItem[] = customerMembersData.users.result
        .filter((u) => !existingEmails.has(u.email?.toLowerCase()))
        .map((u) => ({
          mapping_id: u.mapping_id,
          email: u.email,
          first_name: u.first_name,
          last_name: u.last_name,
          display_name: u.display_name,
          is_active: u.is_active,
          is_locked: u.is_locked,
          member_type: "customer" as const,
        }));

      const combined = [...resources, ...users];

      if (!searchQuery.trim()) {
        return combined.filter((cm) => cm.is_active && !cm.limited_access);
      }
      return combined;
    }, [customerMembersData, membersData, searchQuery]);

    // ─── Inline derived values (no atom sync needed) ────────────────
    const matchingMembersCount = availableCustomerMembers.length;
    const isSearchingCustomerMembers =
      isDebouncePending || isCustomerMembersLoading;

    // ─── Email validation ───────────────────────────────────────────
    const trimmedQuery = searchQuery.trim();

    const isValidEmail = useMemo(() => {
      if (!trimmedQuery) return false;
      return emailSchema.isValidSync(trimmedQuery);
    }, [trimmedQuery]);

    const isEmailAlreadyAdded = useMemo(() => {
      if (!isValidEmail) return false;
      const lower = trimmedQuery.toLowerCase();
      if (extEmails.includes(lower)) return true;
      return (membersData ?? []).some((m) => m.email?.toLowerCase() === lower);
    }, [isValidEmail, trimmedQuery, extEmails, membersData]);

    const hasMatchingMembers =
      filteredCurrentMembers.length > 0 || matchingMembersCount > 0;

    const showEmailDropdown =
      isValidEmail &&
      !isEmailAlreadyAdded &&
      !hasMatchingMembers &&
      !isSearchingCustomerMembers;

    // ─── Handlers ───────────────────────────────────────────────────
    const handleSearchChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
      },
      [],
    );

    const handleAddExternalEmail = useCallback(() => {
      if (!showEmailDropdown) return;
      addExtEmail(trimmedQuery);
      setSearchQuery("");
    }, [showEmailDropdown, trimmedQuery, addExtEmail]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter" && showEmailDropdown) {
          e.preventDefault();
          handleAddExternalEmail();
        }
      },
      [showEmailDropdown, handleAddExternalEmail],
    );

    const handleToggleAvailable = useCallback(
      (userId: string) => {
        if (selectedIds.has(userId)) {
          toggleSelection(userId);
        } else {
          const cm = availableCustomerMembers.find(
            (m) => m.mapping_id === userId,
          );
          if (cm) {
            addToCache({
              user_id: cm.mapping_id,
              display_name: cm.display_name,
              member_type: cm.member_type,
              member_source: "cc",
              email: cm.email,
            });
          }
        }
      },
      [selectedIds, availableCustomerMembers, addToCache, toggleSelection],
    );

    // ─── Close with auto-save ───────────────────────────────────────
    const handleClose = useCallback(async () => {
      if (isSaving) return;

      if (hasChanges && membersData) {
        setIsSaving(true);
        try {
          const { addedIds, removableIds } = computeMemberDiffs(
            membersData,
            selectedIds,
          );
          const addItems = buildAddItems(addedIds, pendingMembers, extEmails);
          const hasUpdates = addItems.length > 0 || removableIds.length > 0;

          if (hasUpdates) {
            const result = await updateMembers({
              service_id: serviceId,
              thread_id: threadId,
              add: addItems.length > 0 ? addItems : undefined,
              remove: removableIds.length > 0 ? removableIds : undefined,
            });
            const parts = buildSuccessMessage(result);
            if (parts.length > 0) toast.success(parts.join(", "));
          }
        } catch (error: unknown) {
          if (error instanceof Error) {
            toast.error(error.message);
          } else {
            toast.error("Failed to update participants");
          }
        } finally {
          setIsSaving(false);
        }
      }

      // Reset local state only — no shared atoms are touched
      setSearchQuery("");
      setSelectedIds(new Set());
      setExtEmails([]);
      setPendingMembers(new Map());
      setInitialIds(new Set());
      onClose();
    }, [
      isSaving,
      hasChanges,
      membersData,
      selectedIds,
      pendingMembers,
      extEmails,
      updateMembers,
      serviceId,
      threadId,
      onClose,
    ]);

    // ─── No results state ───────────────────────────────────────────
    const showNoResults =
      searchQuery.length > 0 &&
      !isCustomerMembersLoading &&
      filteredCurrentMembers.length === 0 &&
      availableCustomerMembers.length === 0;

    return (
      <Popover
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "bottom", horizontal: "left" }}
        PaperProps={{
          sx: {
            width: 370,
            maxHeight: 480,
            borderRadius: "8px",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          },
        }}
      >
        <Typography
          variant="body-small"
          sx={{ p: "8px 12px", color: theme.palette.lightBg.low }}
        >
          Add participants
        </Typography>
        {/* ─── Search ─────────────────────────────────────────────── */}
        <Box
          sx={{
            p: "8px 12px",
          }}
        >
          <SearchInput
            fullWidth
            name="popover-member-search"
            placeholder="Search participants or add an external mail ID"
            value={searchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleKeyDown}
            hideHelperBox
            autoFocus
            inputProps={{ "aria-label": "Search participants" }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: theme.palette.neutral["005"],
                "& fieldset": {
                  borderColor: theme.palette.neutral["010"],
                },
              },
            }}
          />

          {/* Email dropdown */}
          {showEmailDropdown && (
            <Paper
              elevation={2}
              sx={{ mt: "4px", borderRadius: "8px", overflow: "hidden" }}
            >
              <Box
                onClick={handleAddExternalEmail}
                sx={{
                  padding: "10px 16px",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: theme.palette.neutral["005"],
                  },
                }}
              >
                <Typography
                  variant="body-small"
                  sx={{ color: theme.palette.primary.main }}
                >
                  Use this address : @{searchQuery}
                </Typography>
              </Box>
              <Box sx={{ padding: "10px 16px" }}>
                <Typography
                  variant="body-small"
                  sx={{ color: theme.palette.lightBg.low }}
                >
                  We couldn&apos;t find the person you were looking for.
                </Typography>
              </Box>
            </Paper>
          )}

          {/* External email chips */}
          {extEmails.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                mt: "8px",
              }}
            >
              {extEmails.map((email) => (
                <ChipV2
                  key={email}
                  variant="info"
                  clearable
                  onClear={() => removeExtEmail(email)}
                >
                  {email}
                </ChipV2>
              ))}
            </Box>
          )}

          {/* External email warning */}
          {extEmails.length > 0 && (
            <Stack
              direction="row"
              sx={{
                mt: "8px",
                backgroundColor: orange[50],
                border: `1px solid ${orange[800]}`,
                borderRadius: "4px",
                px: "16px",
                py: "8px",
                gap: "8px",
                alignItems: "center",
              }}
            >
              <InfoIcon sx={{ fontSize: "20px" }} htmlColor={orange[800]} />
              <Typography variant="body-small" sx={{ color: orange[800] }}>
                This is an external mail ID
              </Typography>
            </Stack>
          )}
        </Box>

        {/* ─── Scrollable member list ─────────────────────────────── */}
        <Box sx={{ overflow: "auto", height: "400px" }}>
          <AvailableToAddList
            members={availableCustomerMembers}
            selectedIds={selectedIds}
            onToggle={handleToggleAvailable}
            threadId={threadId}
          />

          {/* No results */}
          {showNoResults && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: "24px",
              }}
            >
              <Typography
                variant="body-medium"
                sx={{ color: theme.palette.lightBg.low }}
              >
                No results found
              </Typography>
            </Box>
          )}
        </Box>

        {/* ─── Saving indicator ───────────────────────────────────── */}
        {isSaving && (
          <Box
            sx={{
              p: "8px",
              textAlign: "center",
              borderTop: `1px solid ${theme.palette.neutral["015"]}`,
            }}
          >
            <Typography
              variant="body-small"
              sx={{ color: theme.palette.lightBg.low }}
            >
              Saving changes...
            </Typography>
          </Box>
        )}
      </Popover>
    );
  },
);

ChatMembersPopover.displayName = "ChatMembersPopover";
